import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  User, FolderOpen, BarChart2, Medal, Briefcase,
  Kanban, CreditCard, Settings, Bell, HelpCircle, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_ITEMS = [
  { to: '/dashboard/profile',        label: 'Profile',        icon: User },
  { to: '/dashboard/projects',       label: 'My Projects',    icon: FolderOpen },
  { to: '/dashboard/analytics',      label: 'Analytics',      icon: BarChart2 },
  { to: '/dashboard/badges',         label: 'My Badges',      icon: Medal },
  { to: '/dashboard/services',       label: 'Services',       icon: Briefcase },
  { to: '/dashboard/pipeline',       label: 'Pipeline',       icon: Kanban },
  { to: '/dashboard/billing',        label: 'Billing',        icon: CreditCard },
  { to: '/dashboard/settings',       label: 'Settings',       icon: Settings },
  { to: '/dashboard/notifications',  label: 'Notifications',  icon: Bell },
  { to: '/dashboard/support',        label: 'Support',        icon: HelpCircle },
];

export default function MobileMoreDrawer({ isOpen, onClose }) {
  const { user, loading } = useAuth();

  // During loading, read cached user from localStorage as fallback
  const effectiveUser = user || (() => {
    try { return JSON.parse(localStorage.getItem('vw_user') || 'null'); } catch { return null; }
  })();

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`mob-drawer-backdrop${isOpen ? ' mob-drawer-backdrop-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Bottom sheet */}
      <div
        className={`mob-drawer${isOpen ? ' mob-drawer-open' : ''}`}
        data-testid="mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Drag handle */}
        <div className="mob-drawer-handle" aria-hidden="true" />

        {/* Header */}
        <div className="mob-drawer-header">
          <span className="mob-drawer-title">Menu</span>
          <button className="mob-drawer-close" onClick={onClose} aria-label="Close menu" data-testid="drawer-close">
            ✕
          </button>
        </div>

        {/* User info */}
        {user && (
          <div className="mob-drawer-user" data-testid="drawer-user-row">
            {user.avatar_url ? (
              <img src={user.avatar_url} className="mob-drawer-avatar" alt="" />
            ) : (
              <div className="mob-drawer-avatar mob-drawer-avatar-init">
                {(user.display_name || user.email || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="mob-drawer-name">{user.display_name || 'User'}</div>
              <div className="mob-drawer-email">{user.email}</div>
            </div>
          </div>
        )}

        <div className="mob-drawer-divider" />

        {/* Nav items */}
        <nav className="mob-drawer-nav">
          {DRAWER_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) => `mob-drawer-item${isActive ? ' mob-drawer-item-active' : ''}`}
              data-testid={`drawer-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon size={18} strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          ))}

          {!loading && (effectiveUser?.is_admin || effectiveUser?.role === 'manager') && (
            <>
              <div className="mob-drawer-section-label">Admin</div>
              <NavLink
                to="/admin"
                onClick={onClose}
                className={({ isActive }) => `mob-drawer-item${isActive ? ' mob-drawer-item-active' : ''}`}
                data-testid="drawer-admin-panel"
              >
                <ShieldCheck size={18} strokeWidth={1.6} />
                <span>Admin Panel</span>
              </NavLink>
            </>
          )}
        </nav>
      </div>
    </>
  );
}
